import type { Project } from './projects';

export type PillarId = 'websites' | 'mobile' | 'cloud' | 'security';

export interface Pillar {
  id: PillarId;
  title: string;
  shortTitle: string;
  blurb: string;
  bullets: string[];
  relatedProject: Project['slug'];
  relatedLabel: string;
  // Posts carrying any of these tags are listed under the pillar on /services.
  blogTags: string[];
}

export const pillars: Pillar[] = [
  {
    id: 'websites',
    title: 'Websites & web apps',
    shortTitle: 'Websites',
    blurb: 'Personalized websites custom-built for your brand, audience, and goals, not pulled from a template.',
    bullets: [
      'Custom design & development',
      'Responsive across every device',
      'SEO and Core Web Vitals tuning',
      'CMS or headless content options',
      'Analytics and conversion tracking',
    ],
    relatedProject: 'rbcarcare',
    relatedLabel: 'RB Car Care',
    blogTags: ['web design', 'conversion', 'páginas web'],
  },
  {
    id: 'mobile',
    title: 'Mobile apps & APIs',
    shortTitle: 'Mobile apps',
    blurb: 'Native and cross-platform mobile apps backed by APIs we own end-to-end.',
    bullets: [
      'iOS & Android (React Native or native)',
      'Versioned REST and GraphQL APIs',
      'Push notifications & deep links',
      'Offline-first data sync',
      'App Store & Play Store delivery',
    ],
    relatedProject: 'likefamily',
    relatedLabel: 'Like Family’s mobile-ready APIs',
    blogTags: ['mobile apps'],
  },
  {
    id: 'cloud',
    title: 'Cloud & DevOps',
    shortTitle: 'Cloud & DevOps',
    blurb: 'Infrastructure, CI/CD, and the operational glue that keeps your platform fast and online.',
    bullets: [
      'AWS, Vercel, Supabase, and bare-metal',
      'CI/CD pipelines and zero-downtime deploys',
      'Infrastructure as code',
      'Monitoring, logging, and alerting',
      'Cloud migration & cost optimization',
    ],
    relatedProject: 'likefamily',
    relatedLabel: 'Like Family',
    blogTags: ['cloud', 'DevOps', 'hosting'],
  },
  {
    id: 'security',
    title: 'Cybersecurity',
    shortTitle: 'Cybersecurity',
    blurb: 'Security built in from day one, and an honest review for code already in production.',
    bullets: [
      'Authentication & SSO (magic link, OAuth, Apple)',
      'Role and permission systems',
      'Vulnerability assessment & secure code review',
      'Rate limiting, CAPTCHA, WAF & bot defense',
      'Audit logging, login tracking, incident response',
    ],
    relatedProject: 'likefamily',
    relatedLabel: 'Like Family',
    blogTags: ['security', 'data privacy'],
  },
];

export interface SecondaryOffer {
  title: string;
  blurb: string;
  bullets: string[];
}

export const secondaryOffers: SecondaryOffer[] = [
  {
    title: 'E-commerce',
    blurb: 'Storefronts and checkout flows engineered to convert, not just look good.',
    bullets: [
      'Custom storefront or Shopify/Stripe integration',
      'Secure payment & subscription flows',
      'Inventory, orders, and customer accounts',
    ],
  },
  {
    title: 'UX / UI design',
    blurb: 'User-centered design that turns complex workflows into interfaces people enjoy using.',
    bullets: ['Wireframes & interactive prototypes', 'Visual design & branding', 'Reusable design systems'],
  },
  {
    title: 'Maintenance & support',
    blurb: 'Ongoing support, security patches, and feature work for what we build, or what you already run.',
    bullets: ['Email or ticket-based support', 'Performance & uptime monitoring', 'Security patches & updates'],
  },
];

export interface StackGroup {
  label: string;
  items: string[];
}

export const stackGroups: StackGroup[] = [
  { label: 'Frontend', items: ['React', 'TypeScript', 'Next.js', 'React Native', 'Tailwind CSS', 'styled-components'] },
  { label: 'Backend', items: ['Node.js', 'Express', 'Laravel (PHP 8.x)', 'REST APIs', 'WebSockets', 'Background jobs'] },
  { label: 'Data', items: ['PostgreSQL', 'MySQL', 'Supabase', 'Redis', 'Object storage'] },
  {
    label: 'Cloud & DevOps',
    items: ['AWS', 'Vercel', 'Docker', 'CI/CD pipelines', 'Infrastructure as code', 'Monitoring & logging'],
  },
  {
    label: 'Cybersecurity',
    items: [
      'Magic-link & OAuth auth',
      'Role / permission systems',
      'Cloudflare Turnstile',
      'Rate limiting & WAF',
      'Audit logging',
      'Secure code review',
    ],
  },
];
